/**
 * App Lifecycle Service - Orchestrates application initialization and shutdown
 * 
 * Responsibilities:
 * - Boot sequence coordination
 * - Service initialization order
 * - Cleanup on app shutdown
 * 
 * Architecture:
 * - Infrastructure layer
 * - Called from main.tsx on app mount/unmount
 */

import { logger } from '../utils/logger'



export interface LifecycleService {
  initialize(): Promise<void>
  cleanup(): void
}

export class AppLifecycleService {
  private services: LifecycleService[] = []
  private isInitialized = false
  private initializeListeners: Set<() => void> = new Set()

  /**
   * Register a service to be initialized/cleaned up
   */
  registerService(service: LifecycleService): void {
    this.services.push(service)
  }

  /**
   * Initialize all services in order
   */
  async initialize(): Promise<void> {
    if (this.isInitialized) {
      logger.warn('[AppLifecycle] Already initialized')
      return
    }

    logger.info('[AppLifecycle] Starting application initialization...')

    try {
      // 1. Core services are registered in services.ts and initialized here in order
      logger.debug('[AppLifecycle] Starting service initialization sequence...')

      // 2. Initialize registered services sequentially
      for (const service of this.services) {
        logger.debug(`[AppLifecycle] Initializing service...`)
        await service.initialize()
      }

      this.isInitialized = true
      logger.info('[AppLifecycle] ✅ Application initialized successfully')
      
      // Notify listeners
      this.notifyInitialized()
    } catch (error) {
      logger.error('[AppLifecycle] ❌ Initialization failed:', error)
      throw error
    }
  }

  /**
   * Cleanup all services (in reverse order)
   */
  cleanup(): void {
    if (!this.isInitialized) {
      return
    }

    logger.info('[AppLifecycle] Cleaning up application...')

    // Cleanup services in reverse order
    for (let i = this.services.length - 1; i >= 0; i--) {
      try {
        logger.debug(`[AppLifecycle] Cleaning up service ${i}...`)
        this.services[i].cleanup()
      } catch (error) {
        logger.error(`[AppLifecycle] Error cleaning up service ${i}:`, error)
      }
    }

    // Leader election cleanup not needed (handled internally)

    this.isInitialized = false
    this.initializeListeners.clear()
    logger.info('[AppLifecycle] Cleanup complete')
  }

  /**
   * Register a callback to be called when initialization is complete
   */
  onInitialized(callback: () => void): () => void {
    this.initializeListeners.add(callback)
    
    // If already initialized, call immediately
    if (this.isInitialized) {
      callback()
    }
    
    return () => this.initializeListeners.delete(callback)
  }

  /**
   * Check if application is initialized
   */
  isReady(): boolean {
    return this.isInitialized
  }

  private notifyInitialized(): void {
    this.initializeListeners.forEach(listener => {
      try {
        listener()
      } catch (error) {
        logger.error('[AppLifecycle] Error in initialized listener:', error)
      }
    })
  }
}

// Singleton instance
export const appLifecycleService = new AppLifecycleService()
